/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import { Autocomplete, RenderInputParams } from '@material-ui/lab';
import { TextField, CircularProgress } from '@material-ui/core';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import { showNotification } from '../../App';
import { IAutoCompleteItem } from '../../typings/autoComplete';
import { client } from '../..';
import { FetchPolicy } from 'apollo-client';
import gql from 'graphql-tag';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
        width: '100%'
    },
  })
);

interface SourceSelectorProps {
    disabled?: boolean;
    required?: boolean;
    multiple?: boolean;
    // tslint:disable-next-line: no-any
    value?: IAutoCompleteItem | any; // value only available in single select
    error?: boolean;
    onSelection?: (value: IAutoCompleteItem | IAutoCompleteItem[], name: string) => void;
    className?: string;
    helperText?: string;
    label: string;
    name: string;
    disablePortal?: boolean;
    disableCloseOnSelect?: boolean;
    onBlur?: React.FocusEventHandler<HTMLDivElement>;
    variant?: 'standard' | 'outlined' | 'filled';
    size?: 'small' | 'medium';
}

interface SourceSelectorState {
    isLoading: boolean;
    options: IAutoCompleteItem[];
}

export const SourceSelector: React.FC<SourceSelectorProps> = props => {

    const classes = useStyles();

    const [state, setState] = useState<SourceSelectorState>({
        isLoading: false,
        options: [],
    });

    useEffect(() => {   
        fetchData();
    // tslint:disable-next-line: align
    }, []);

    const fetchData = () => {

        setState({
            ...state,
            isLoading: true
        });

        retrieveEnquirySourceData(
            false,
            // tslint:disable-next-line: no-console
            (data) => onRetrieveSource(data),
            // tslint:disable-next-line:no-any
            function (reason: any): void {
                showNotification(null, reason, 'error');
            }
        );
    };

    const onRetrieveSource = (data: EnquirySourceData) => {
        setState({
            ...state,
            options: data.enquiry.sources.map( (source: Source) => (
                {
                    value: source.guidId, 
                    label: source.description,
                    secondaryValue: source.isReferrer
                })),
            isLoading: false
        });
    };

    // tslint:disable-next-line: no-any
    const handleChange = (event: React.ChangeEvent<{}>, value: any) => {
        // tslint:disable-next-line: no-console
        console.log('onChange', value);
        if (props.onSelection) {
            props.onSelection(value, props.name);
        }
    };

    const getInputProps = (params: RenderInputParams, isLoading?: boolean) => {
        return  ({   
            ...params.InputProps,
            endAdornment: (
                <React.Fragment>
                  {isLoading ? <CircularProgress color="primary" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),     
         });
    };

    return (
        <div className={`${classes.root} ${props.className}`}>
            { props.multiple ? 
                (
                    <Autocomplete
                        // [] (not null) is MUI's "nothing selected" sentinel for multiple={true}.
                        value={Array.isArray(props.value) ? props.value : []}
                        multiple={true}
                        loading={state.isLoading}
                        disabled={props.disabled}
                        onChange={handleChange}
                        options={state.options}
                        disableCloseOnSelect={props.disableCloseOnSelect}
                        disablePortal={props.disablePortal} // to Stop the dropdown select from closing the form
                        // tslint:disable-next-line: jsx-no-lambda
                        getOptionLabel={(option: IAutoCompleteItem) => option.label}     
                        getOptionSelected={(option, value) => value.value === option.value}  
                        onBlur={props.onBlur}
                        // tslint:disable-next-line: jsx-no-lambda
                        renderInput={params => (
                            <TextField
                                {...params}
                                label={props.label}
                                variant={props.variant ? props.variant : 'standard'}
                                size={props.size ? props.size : 'medium'}
                                fullWidth={true}
                                margin="none"
                                required={props.required}
                                InputProps={getInputProps(params, state.isLoading)}
                                error={props.error}
                                helperText={props.helperText}
                            />
                        )}
                        // tslint:disable-next-line: jsx-no-lambda
                        renderOption={(option, { inputValue }) => {
                            const matches = match(option.label, inputValue);
                            const parts = parse(option.label, matches);

                            return (
                                <div>
                                    {parts.map((part, index) => (
                                        <span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
                                            {part.text}
                                        </span>
                                    ))}
                                </div>
                            );
                        }}
                    />
                )
                :
                (
                    <Autocomplete
                        // Explicit bug report: the raw field value (e.g. '' before anything is
                        // selected) was passed straight through - not an {label,value}-shaped
                        // object matching `options` - causing MUI's getOptionLabel to be called
                        // on a non-object and log "getOptionLabel returned undefined...". null
                        // is MUI's real "nothing selected" sentinel.
                        value={props.value && typeof props.value === 'object' ? props.value : null}
                        loading={state.isLoading}
                        disabled={props.disabled}
                        onChange={handleChange}
                        options={state.options}
                        disableCloseOnSelect={props.disableCloseOnSelect}
                        disablePortal={props.disablePortal} // to Stop the dropdown select from closing the form
                        // tslint:disable-next-line: jsx-no-lambda
                        getOptionLabel={(option: IAutoCompleteItem) => option.label}
                        getOptionSelected={(option, value) => value.value === option.value}
                        onBlur={props.onBlur}
                        // tslint:disable-next-line: jsx-no-lambda
                        renderInput={params => (
                            <TextField
                                {...params}
                                label={props.label}
                                variant={props.variant ? props.variant : 'standard'}
                                size={props.size ? props.size : 'medium'}
                                fullWidth={true}
                                margin="none"
                                required={props.required}
                                InputProps={getInputProps(params, state.isLoading)}
                                error={props.error}
                                helperText={props.helperText}
                            />
                        )}
                        // tslint:disable-next-line: jsx-no-lambda
                        renderOption={(option, { inputValue }) => {
                            const matches = match(option.label, inputValue);
                            const parts = parse(option.label, matches);
                    
                            return (
                                <div>
                                {parts.map((part, index) => (
                                    <span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
                                    {part.text}
                                    </span>
                                ))}
                                </div>
                            );
                        }}
                    />
                )
            }
        </div>
    );
};

export function retrieveEnquirySourceData(
    refreshData: boolean,
    onSuccess: (data: EnquirySourceData) => void,
    // tslint:disable-next-line:no-any
    onError: (reason: any) => void
): void  {
    var fetchPolicy: FetchPolicy = refreshData === true ? 'network-only' : 'cache-first';
    client
        .query({
            query: EnquirySourceDataQuery,
            variables: {},
            fetchPolicy: fetchPolicy,
        })
        // tslint:disable-next-line:no-any
        .then((results: { data: any }) => {
            onSuccess(results.data);
        })
        // tslint:disable-next-line:no-any
        .catch((reason: any) => {
            onError(reason);
        });
}

export interface EnquirySourceData {
    loading?: boolean;
    networkStatus?: number;
    enquiry: Enquiry;
}
export interface Enquiry {
    sources: Source[];
}

export interface Source {
    guidId:      string;
    description: string;
    isReferrer:  boolean;
}

const EnquirySourceDataQuery = gql`
    query enquirySource {
        enquiry{
            sources{
                guidId,
                description,
                isReferrer
            }
        }
    }
`;